/**
 * @file        src/pages/account/WalletManagement.jsx
 * @description Admin management of pooled COMPANY wallets (business_accounts) —
 *              one shared balance every employee of a company books against.
 *              Search by name/brand, view balance + ledger, add money
 *              (password-confirmed), and freeze/unfreeze. Individual retail/user
 *              wallets are managed separately under Wallet Management (Users).
 *              Backend: /api/wallets/companies/*.
 * @author      Kinetika Labs
 * @created     2026-07-23
 * @changelog
 *   - 2026-08-03 Claude — Crumb: Account Management -> Wallet Management (new
 *                          sidebar group).
 *   - 2026-08-03 Claude — Brand filter + brand column read the tenant's brand
 *                          registry (BrandSelect/useBrands); dropped the
 *                          hardcoded Neon/Beyond pair.
 *   - 2026-08-04 Claude — Credit expiry DatePicker gets min=today + a submit
 *                          check: past expiry is rejected server-side too.
 *   - 2026-08-04 Claude — Agent retirement (CR E8): dropped the agentCode
 *                          display (the field no longer comes back from the
 *                          company search API); falls back to email only.
 *   - 2026-09-15 Claude — API errors read via apiErrorMessage(): the error handler returns {error:{code,message}}, and putting that object into state/toast threw "Objects are not valid as a React child", blanking the whole app.
 *   - 2026-09-15 Claude — Stop the browser password manager autofilling the
 *                          admin's email into Reason: it is the text field
 *                          nearest the password box, so it was being treated
 *                          as the username. Reason opts out; a hidden decoy
 *                          username input takes the fill instead.
 *   - 2026-09-15 Claude — Added the one-line note stating the spend order a pool
 *                          actually follows at checkout (wallet → credit line →
 *                          gateway, oldest-expiring money first) and that an
 *                          enabled company pool is spent regardless of the
 *                          storefront's "Use Wallet" tick.
 *   - 2026-09-17 Claude — The re-auth field is now <PasswordInput> (reveal
 *                          toggle). The autofill suppressors are unchanged.
 *   - 2026-09-24 Claude — Ledger Type column prints the shared wallet label
 *                          (lib/walletTypes) instead of the raw enum value
 *                          ("booking_debit", "admin_credit").
 *   - 2026-09-25 Claude — `keepPreviousData: true` is a React Query **v4** option
 *                          and this workspace is on v5 (5.101), where no such
 *                          option exists and it was silently ignored. So every
 *                          page / filter / search change dropped `data` to
 *                          undefined, the table was swapped for six skeleton rows
 *                          and the panel collapsed ~700px before growing back —
 *                          the "pages flicker / the page moves" report. v5 spells
 *                          it `placeholderData: keepPreviousData` (a FUNCTION
 *                          imported from the package, not a boolean).
 */
import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import { WALLET_NOTE } from '../../lib/pricingNotes';
import Icon from '../../components/Icon';
import Chip from '../../components/Chip';
import BrandSelect from '../../components/BrandSelect';
import DatePicker from '../../components/DatePicker';
import Pager from '../../components/Pager';
import TableSkeleton from '../../components/TableSkeleton';
import { get, post, apiErrorMessage } from '../../api/client';
import { useBrands } from '../../api/brands';
import { toast } from '../../lib/toast';
import { inr } from '../../lib/format';
import { walletTypeLabel } from '../../lib/walletTypes';
import PasswordInput from '../../components/PasswordInput';

const statusTone = (s) => (s === 'active' ? 'ok' : s === 'frozen' ? 'pend' : 'rej');
const money = (v) => (typeof inr === 'function' ? inr(v) : `₹${Number(v || 0).toLocaleString('en-IN')}`);
const todayKey = () => {
  const t = new Date();
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
};

export default function WalletManagement() {
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [q, setQ] = useState('');
  const [applied, setApplied] = useState('');
  const [brand, setBrand] = useState('');
  const [manage, setManage] = useState(null); // { id, name }
  const { labelOf } = useBrands();

  const params = { page, pageSize, ...(applied ? { q: applied } : {}), ...(brand ? { brand } : {}) };
  const { data, isLoading } = useQuery({
    queryKey: ['company-wallets', params],
    queryFn: () => get('/wallets/companies', { params }),
    placeholderData: keepPreviousData,
  });
  const rows = data?.data || [];
  const total = data?.meta?.total || 0;

  return (
    <>
      <PageHead title="Company Wallets" subtitle="Wallet Management · pooled B2B / corporate wallets" />

      <div className="notice-banner info">{WALLET_NOTE}</div>

      <article className="panel glass">
        <form className="search-row" onSubmit={(e) => { e.preventDefault(); setPage(1); setApplied(q); }}>
          <div className="field">
            <label>Search</label>
            <input placeholder="Company name, email" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <div className="field">
            <label>Brand</label>
            <BrandSelect
              value={brand}
              onChange={(v) => { setBrand(v); setPage(1); }}
              withAll
            />
          </div>
          <button className="btn primary" type="submit"><Icon name="search" /> Search</button>
        </form>

        <div className="table-wrap">
          {isLoading ? <TableSkeleton /> : (
            <table className="table">
              <thead>
                <tr><th>#</th><th>Company</th><th>Brand</th><th>Type</th><th>Balance</th><th>Wallet</th><th>Action</th></tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={7} className="empty">No company wallets found.</td></tr>
                ) : rows.map((r, idx) => (
                  <tr key={r.id}>
                    <td className="mono">{(page - 1) * pageSize + idx + 1}</td>
                    <td><b>{r.name}</b><small className="muted db">{r.email || ''}</small></td>
                    <td><Chip tone="ok">{labelOf(r.brand)}</Chip></td>
                    <td>{r.accountType === 'corporate' ? 'Corporate' : 'B2B'}</td>
                    <td><b>{money(r.balance)}</b></td>
                    <td>
                      {r.walletEnabled
                        ? <Chip tone={statusTone(r.walletStatus)}>{r.walletStatus || 'active'}</Chip>
                        : <span className="muted">disabled</span>}
                    </td>
                    <td>
                      <button className="btn xs ghost" onClick={() => setManage({ id: r.id, name: r.name })}>
                        <Icon name="wallet" /> Manage
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <Pager page={page} pageSize={pageSize} total={total} onPage={setPage} />
      </article>

      {manage ? <ManageCompanyWallet ctx={manage} onClose={() => setManage(null)} /> : null}
    </>
  );
}

function ManageCompanyWallet({ ctx, onClose }) {
  const qc = useQueryClient();
  const base = `/wallets/companies/${ctx.id}`;
  const key = ['company-wallets', 'detail', ctx.id];

  const detail = useQuery({ queryKey: key, queryFn: () => get(base) });
  const txns = useQuery({ queryKey: [...key, 'txns'], queryFn: () => get(`${base}/transactions`, { params: { page: 1, pageSize: 15 } }) });

  const wallet = detail.data?.data?.wallet;
  const company = detail.data?.data?.company;
  const rows = txns.data?.data || [];

  const [form, setForm] = useState({ amount: '', expiryDate: '', reason: '', password: '' });
  const [err, setErr] = useState('');
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: key });
    qc.invalidateQueries({ queryKey: ['company-wallets'] });
  };

  const credit = useMutation({
    mutationFn: () => post(`${base}/credit`, {
      amount: Number(form.amount),
      expiryDate: form.expiryDate || '',
      reason: form.reason.trim(),
      password: form.password,
    }),
    onSuccess: () => { toast.ok('Money added', 'The company wallet was credited.'); setForm({ amount: '', expiryDate: '', reason: '', password: '' }); invalidate(); },
    onError: (e) => setErr(e?.response?.data?.error?.code === 'BAD_CREDENTIALS' ? 'Incorrect password.' : apiErrorMessage(e, 'Could not add money.')),
  });

  const setStatus = useMutation({
    mutationFn: (action) => post(`${base}/status`, { action }),
    onSuccess: () => { toast.ok('Updated', 'Wallet status changed.'); invalidate(); },
    onError: (e) => toast.err('Failed', apiErrorMessage(e, 'Could not change status.')),
  });

  const submitCredit = (e) => {
    e.preventDefault(); setErr('');
    if (!(Number(form.amount) > 0)) { setErr('Enter an amount greater than zero.'); return; }
    if (form.expiryDate && form.expiryDate < todayKey()) { setErr('Expiry date cannot be in the past.'); return; }
    if (!form.reason.trim()) { setErr('A reason is required.'); return; }
    if (!form.password) { setErr('Confirm with your password.'); return; }
    credit.mutate();
  };

  const frozen = wallet?.status === 'frozen';

  return (
    <div className="lk-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="lk-modal panel glass" style={{ maxWidth: 720 }}>
        <div className="lk-modal-head">
          <h3>Company Wallet · {company?.name || ctx.name}</h3>
          <button className="ib" aria-label="Close" onClick={onClose}><Icon name="x" /></button>
        </div>

        <div className="kpi-row" style={{ display: 'flex', gap: 16, margin: '8px 0 16px', flexWrap: 'wrap' }}>
          <div className="kpi"><small className="muted">Balance</small><div className="grad-text" style={{ fontSize: 24 }}>{money(wallet?.balance)}</div></div>
          <div className="kpi"><small className="muted">Status</small><div><Chip tone={statusTone(wallet?.status)}>{wallet?.status || 'active'}</Chip></div></div>
          <div className="kpi"><small className="muted">Payment mode</small><div>{company?.paymentMode === 'wallet_only' ? 'Wallet only' : 'Wallet + gateway'}</div></div>
          {company && !company.walletEnabled ? <div className="kpi"><Chip tone="rej">Wallet disabled for this company</Chip></div> : null}
        </div>

        <form className="form-grid" onSubmit={submitCredit}>
          <div className="field full"><label className="section-label">Add money to the pool</label></div>
          <div className="field">
            <label>Amount (₹) <span className="req">*</span></label>
            <input type="number" min="1" step="0.01" value={form.amount} onChange={set('amount')} placeholder="e.g. 500000" />
          </div>
          <div className="field">
            <label>Expiry (optional)</label>
            <DatePicker value={form.expiryDate} onChange={(v) => setForm({ ...form, expiryDate: v })} min={todayKey()} />
          </div>
          <div className="field full">
            <label>Reason <span className="req">*</span></label>
            <input
              value={form.reason}
              onChange={set('reason')}
              name="reason"
              autoComplete="off"
              data-lpignore="true"
              data-1p-ignore
              placeholder="e.g. Advance top-up for Q3 corporate travel"
            />
          </div>
          {/* Password managers pick the text input nearest the password box as
              the "username" and fill the signed-in admin's email into it — that
              is why Reason used to open pre-filled. This decoy gives them a
              real username target; it is never read or submitted. */}
          <input
            type="text"
            name="username"
            autoComplete="username"
            tabIndex={-1}
            aria-hidden="true"
            style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
          />
          <div className="field full">
            <label>Your password <span className="req">*</span></label>
            {/* NEVER pre-filled. `autoComplete="current-password"` is the exact
                token that tells a browser and every password manager "this is the
                login password for this site" — so this box opened already filled
                with the signed-in admin's saved password, and one click on "Add
                money" moved real money with no re-authentication at all. The
                whole point of the field is that the person at the keyboard proves
                it is them, every time. "new-password" is the standard suppressor;
                the data-* attributes cover LastPass and 1Password, which ignore
                it. The decoy above still absorbs the "username" fill. */}
            <PasswordInput
              value={form.password}
              onChange={set('password')}
              placeholder="Confirm this action"
              autoComplete="new-password"
              data-lpignore="true"
              data-1p-ignore
            />
          </div>
          {err ? <div className="field-error full">{err}</div> : null}
          <div className="form-actions full">
            <button type="button" className="btn ghost" onClick={() => setStatus.mutate(frozen ? 'unfreeze' : 'freeze')} disabled={setStatus.isPending}>
              {frozen ? 'Unfreeze wallet' : 'Freeze wallet'}
            </button>
            <button className="btn primary" type="submit" disabled={credit.isPending}>
              {credit.isPending ? 'Adding…' : 'Add money'}
            </button>
          </div>
        </form>

        <div className="table-wrap" style={{ marginTop: 12 }}>
          <label className="section-label">Recent ledger</label>
          <table className="table">
            <thead><tr><th>Date</th><th>Type</th><th>Debit</th><th>Credit</th><th>Balance</th><th>Ref</th></tr></thead>
            <tbody>
              {rows.length === 0 ? <tr><td colSpan={6} className="empty">No transactions yet.</td></tr> : rows.map((t) => (
                <tr key={t.id}>
                  <td className="mono">{new Date(t.createdAt).toLocaleString('en-IN')}</td>
                  <td>{walletTypeLabel(t.type)}</td>
                  <td className="mono">{t.direction === 'debit' ? money(t.amount) : '—'}</td>
                  <td className="mono">{t.direction === 'credit' ? money(t.amount) : '—'}</td>
                  <td className="mono">{money(t.balanceAfter)}</td>
                  <td className="mono">{t.reference || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
